import { Outlet, useOutletContext, useParams } from 'react-router-dom'

import { Features } from 'generated/featureDefinitions'
import { MyProjectsRootPageDocument } from 'generated/resources/graphql'

import AuthorizeUser from 'modules/core/components/AuthorizeUser'
import NotFound from 'modules/core/components/NotFound'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import LoadingState from 'modules/command_bar/components/CommandBar/components/Results/components/LoadingState'
import { useGetProjectsAssignedToWorker } from 'modules/project_management/hooks/projectWorker/useGetProjectsAssignedToWorker'
import { RouteContextParams } from 'modules/project_management/pages/MyProjectsRootPage'
import ProjectsStore from 'modules/project_management/store/models/Projects'

const ProjectsRootPage = () => {
  const { company } = useCurrentSession()
  const { employeeId } = useParams()

  const employeeIdNumber = String(employeeId)

  const outlet = useOutletContext<RouteContextParams>()
  const { projectsAssigned, loading: loadingProjects } = useGetProjectsAssignedToWorker({
    employeeIds: [employeeIdNumber],
    onlyActiveProjects: false,
    currentlyAssigned: false,
    includeSubprojects: true,
  })

  const { data: page, loading: loadingPage } = useQuery(MyProjectsRootPageDocument, {
    variables: {
      companyId: company.id,
      employeeId: employeeIdNumber,
      featureIds: [Features.projects_flexible_tracking],
    },
    skip: !employeeId,
  })
  const hasMyProjectsFeature = !!page?.companies.featureAvailabilitiesConnection.nodes.find(
    (node) => node.id === Features.projects_flexible_tracking
  )?.available

  if (loadingProjects || loadingPage) return <LoadingState />
  if (!page) return <NotFound />
  const projectsStore: ProjectsStore = new ProjectsStore({ projectsAssigned })

  const canManageMyProjects =
    page.projectManagement._policies.flexibleTimeRecordRead.can ||
    page.projectManagement._policies.globalTimeRecordRead.can ||
    page.employees.employee?._policies?.registerFlexibleTracking.can ||
    false

  const isAuthorized =
    (canManageMyProjects && hasMyProjectsFeature && projectsStore.hasProjectsAssigned) ||
    !hasMyProjectsFeature

  const employeeLocationId = page.employees.employee?.locationId

  return (
    <AuthorizeUser authorizeFn={() => company && isAuthorized}>
      <Outlet
        context={{
          tabs: outlet?.tabs || [],
          projectsStore,
          employeeLocationId,
        }}
      />
    </AuthorizeUser>
  )
}

export default ProjectsRootPage
